import { useState } from "react";
import { toast } from "sonner";
import { Mail, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

// Small inline waitlist form for placeholder pages. `source` tags the row in
// the `waitlist` collection so we can segment announcements later (e.g. only
// email people who signed up for `blog` when the first post ships).
export default function WaitlistCapture({ source, headline, subhead }) {
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/waitlist", { email, source });
      setSaved(true);
      toast.success("You're on the list — thank you!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div className="mt-10 rounded-3xl border-2 border-[hsl(var(--sb-sage)/0.5)] bg-[hsl(var(--sb-moss)/0.5)] px-6 py-5"
        data-testid={`waitlist-thanks-${source}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-sage)/0.3)] grid place-items-center shrink-0">
            <Check size={20} className="text-[hsl(var(--sb-forest-2))]" />
          </div>
          <div>
            <h4 className="font-serif text-lg text-[hsl(var(--sb-forest-2))]">You're on the list.</h4>
            <p className="mt-1 text-sm text-muted-foreground">
              We'll email <strong>{email}</strong> the moment this launches — and never for anything else.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-10 rounded-3xl border-2 border-[hsl(var(--sb-gold)/0.4)] bg-[hsl(var(--sb-surface))] p-5 sm:p-6"
      data-testid={`waitlist-form-${source}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-gold)/0.2)] grid place-items-center shrink-0">
          <Mail size={20} className="text-[hsl(var(--sb-gold-2))]" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-xl text-[hsl(var(--sb-forest-2))]">{headline}</h4>
          <p className="mt-1 text-sm text-muted-foreground">{subhead}</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="sb-input flex-1"
              data-testid={`waitlist-email-${source}`}
            />
            <button type="submit" disabled={busy} className="btn-primary whitespace-nowrap"
              data-testid={`waitlist-submit-${source}`}>
              {busy ? "Adding…" : "Notify me"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
